import { useEffect, useMemo, useRef, type ReactNode } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import {
  AppShell,
  AppSwitcher,
  CommandPalette,
  NotificationCenter,
  useBusNotifications,
  useCommandPalette,
  useNotifications,
  ThemeToggle,
  AccountMenu,
  SearchTrigger,
  SidebarNav,
  NavGroup,
  NavItem,
  type CommandItem,
  type NotificationItem,
} from '@sectepe/ui';
import { useAuth } from '../contexts/AuthContext';
import { useBrand } from '../contexts/BrandContext';
import { isHttpsUrl } from '../services/brand';
import { SUITE_APPS } from './suiteApps';
import { NAV_ITEMS, NAV_ACTIONS } from './nav';

/**
 * Wortmarke bzw. Logo fuer den Brand-Slot (White-Label). Haus-Fall: inline
 * "SecTepe"-Wortmarke wie bisher (kein Asset-Import noetig). Fremdmarke:
 * `<img>` bei gesetzter https-Logo-URL, sonst der Markenname als Text.
 */
function Brand() {
  const { brand } = useBrand();
  const logo = isHttpsUrl(brand.logoUrl) ? brand.logoUrl : null;
  return (
    <span style={{ display: 'inline-flex', alignItems: logo ? 'center' : 'baseline', gap: 6, whiteSpace: 'nowrap' }}>
      {logo ? (
        <img src={logo} alt={brand.name} style={{ height: 24, width: 'auto', display: 'block' }} />
      ) : (
        <span style={{ fontFamily: 'var(--sec-font-brand, inherit)', fontWeight: 400, fontSize: '1.05rem' }}>
          {brand.name}
        </span>
      )}
      <span className="sec-subtle" style={{ fontSize: 'var(--sec-fs-sm, 0.85rem)' }}>
        Platform-Admin
      </span>
    </span>
  );
}

/** Einfaches Konto-Menue rechts: E-Mail + Logout (uebernimmt die bisherige Logik). */
function ProfileMenu() {
  const { operator, signOut } = useAuth();
  if (!operator) return null;
  return (
    <AccountMenu
      label={operator.email}
      secondary={operator.readOnly ? 'read-only' : undefined}
      onLogout={signOut}
    />
  );
}

/** Persistente Sidebar: dieselbe Nav-Liste wie die Command-Palette. */
function Sidebar({ extraBadges }: { extraBadges?: Record<string, ReactNode> }) {
  return (
    <SidebarNav>
      <NavGroup>
        {NAV_ITEMS.map((item) => (
          <NavItem key={item.id} as={NavLink} to={item.to} end={item.to === '/'} badge={extraBadges?.[item.id] ?? undefined}>
            {item.label}
          </NavItem>
        ))}
      </NavGroup>
    </SidebarNav>
  );
}

export interface AdminShellProps {
  children: ReactNode;
  /** Optionale Badge-Elemente je Nav-id (z. B. Plan-Change-/Marketplace-Zaehler). */
  navBadges?: Record<string, ReactNode>;
}

/**
 * Operator-Konsole im einheitlichen @sectepe/ui-Rahmen (Saeulen A/B/C):
 * - AppShell: Top-Bar (Brand, App-Switcher, Suche, NotificationCenter, Profil) + Sidebar.
 * - CommandPalette: Ctrl/Cmd+K; Eintraege aus NAV_ITEMS/NAV_ACTIONS navigieren via Router.
 * - NotificationCenter: liest aus dem NotificationProvider; onSelect deep-linkt
 *   (sofern die Benachrichtigung eine Entity mit erkennbarem Ziel traegt).
 */
export function AdminShell({ children, navBadges }: AdminShellProps) {
  const navigate = useNavigate();
  const { open, openPalette, closePalette } = useCommandPalette();
  const { add } = useNotifications();
  const seeded = useRef(false);

  // Live-Bus-Notifications, opt-in im Cookie-Modus. Diese Konsole haelt keinen
  // browser-lesbaren Bearer-Token (oauth2-proxy-Cookie + ForwardAuth), daher
  // verbindet der Adapter OHNE Token und sendet Credentials (Cookies) mit
  // (`withCredentials`). Voraussetzung fuer den Live-Pfad: der Bus-SSE muss
  // SAME-ORIGIN hinter dem oauth2-proxy der SPA erreichbar sein und der Proxy
  // muss den Access-Token upstream injizieren (--set-authorization-header /
  // --pass-access-token), damit bus-gateway `requireKeycloak` einen gueltigen
  // Bearer sieht. Ohne gesetztes VITE_BUS_URL ist der Adapter ein No-Op (nur
  // der Welcome-Seed unten). Siehe docs/app-integration-bus.md.
  const busBase = (import.meta.env.VITE_BUS_URL || '').replace(/\/+$/, '');
  const busSse = busBase ? `${busBase}/sse` : '';
  useBusNotifications({ url: busSse, withCredentials: true, enabled: Boolean(busSse) });

  // Eine einmalige Begruessungs-Benachrichtigung, damit das NotificationCenter
  // auch ohne aktiven Bus (VITE_BUS_URL ungesetzt) sichtbar funktioniert. Das
  // Bus-Wiring ist jetzt opt-in (Cookie-Modus) verdrahtet — live, sobald
  // VITE_BUS_URL gesetzt ist UND das Deployment SSE same-origin mit
  // Bearer-Injection routet. Der Ref-Guard verhindert ein Doppel-Add unter
  // React.StrictMode.
  useEffect(() => {
    if (seeded.current) return;
    seeded.current = true;
    add({
      title: 'Platform-Admin bereit',
      body: 'Drücke Ctrl/Cmd+K für die Befehls-Palette.',
      severity: 'info',
      source: 'platform-admin',
    });
  }, [add]);

  const commands = useMemo<CommandItem[]>(() => {
    const navCommands: CommandItem[] = NAV_ITEMS.map((item) => ({
      id: `nav:${item.id}`,
      label: item.label,
      group: 'Navigation',
      hint: item.to,
      keywords: item.keywords,
      perform: () => navigate(item.to),
    }));
    const actionCommands: CommandItem[] = NAV_ACTIONS.map((action) => ({
      id: `action:${action.id}`,
      label: action.label,
      group: 'Aktionen',
      hint: action.to,
      keywords: action.keywords,
      perform: () => navigate(action.to),
    }));
    return [...navCommands, ...actionCommands];
  }, [navigate]);

  const onNotificationSelect = (item: NotificationItem) => {
    // Deep-Link, wenn die Benachrichtigung eine Tenant-Entity traegt; sonst
    // bleibt der Klick (markiert nur gelesen) ohne Navigation.
    const entity = item.entity;
    if (entity?.type === 'tenant' && entity.id) {
      navigate(`/tenants/${encodeURIComponent(entity.id)}`);
    } else if (entity?.type === 'audit') {
      navigate('/audit');
    }
  };

  return (
    <>
      <AppShell
        brand={<Brand />}
        appSwitcher={<AppSwitcher apps={SUITE_APPS} label="Apps" />}
        search={<SearchTrigger onOpen={openPalette} />}
        actions={<><ThemeToggle /><NotificationCenter onSelect={onNotificationSelect} /></>}
        profile={<ProfileMenu />}
        sidebar={<Sidebar extraBadges={navBadges} />}
      >
        {children}
      </AppShell>

      <CommandPalette open={open} onClose={closePalette} commands={commands} />
    </>
  );
}
